import React, { useState, useMemo } from "react";
import { visibilityRegistry } from "@/config/visibilityRegistry";
import { useVisibility } from "@/contexts/VisibilityContext";
import { Accordion } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import {
  SearchFilterBar,
  PublicModeToggle,
  PageAccordion,
  type FilterLevel,
} from "./god-mode";

export function GodModePanel() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterLevel, setFilterLevel] = useState<FilterLevel>("all");
  const { getVisibility } = useVisibility();

  // Filter registry based on search and filter
  const filteredRegistry = useMemo(() => {
    return visibilityRegistry
      .map((page) => {
        // Filter sections by search query and visibility level
        const matchingSections = page.sections.filter((section) => {
          const matchesSearch =
            !searchQuery ||
            section.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            `${page.id}.${section.id}`.toLowerCase().includes(searchQuery.toLowerCase());

          const visibility = getVisibility(`${page.id}.${section.id}`);
          const matchesFilter = filterLevel === "all" || visibility === filterLevel;

          return matchesSearch && matchesFilter;
        });

        // Also check if page name matches search
        const pageMatchesSearch =
          !searchQuery || page.label.toLowerCase().includes(searchQuery.toLowerCase());

        // Check page visibility for filter
        const pageVisibility = getVisibility(page.id);
        const pageMatchesFilter = filterLevel === "all" || pageVisibility === filterLevel;

        return {
          ...page,
          filteredSections: matchingSections,
          pageMatchesSearch,
          pageMatchesFilter,
        };
      })
      .filter(
        (page) =>
          page.filteredSections.length > 0 ||
          (page.pageMatchesSearch && page.pageMatchesFilter)
      );
  }, [searchQuery, filterLevel, getVisibility]);

  const totalSections = filteredRegistry.reduce(
    (acc, p) => acc + p.filteredSections.length,
    0
  );

  return (
    <div className="mt-6 space-y-6">
      {/* Quick Actions - Public Mode Toggle */}
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-bold">Quick Actions</h3>
          <p className="text-xs text-muted-foreground">Temporary visibility overrides</p>
        </div>
        <PublicModeToggle />
      </section>

      <Separator />

      {/* Search and Filter Bar */}
      <SearchFilterBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterLevel={filterLevel}
        setFilterLevel={setFilterLevel}
      />

      {/* Results Count */}
      {(searchQuery || filterLevel !== "all") && (
        <p className="text-sm text-muted-foreground">
          Showing {totalSections} section{totalSections !== 1 ? "s" : ""} across{" "}
          {filteredRegistry.length} page{filteredRegistry.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* Collapsible Accordion for each page */}
      {filteredRegistry.length > 0 ? (
        <Accordion type="multiple" className="space-y-2">
          {filteredRegistry.map((page) => (
            <PageAccordion
              key={page.id}
              page={page}
              filteredSections={page.filteredSections}
            />
          ))}
        </Accordion>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>No pages or sections match your search.</p>
        </div>
      )}
    </div>
  );
}
